import React from 'react';
import { ClipboardList, PlayCircle, TrendingUp, Download } from 'lucide-react';
import type { OverviewTabProps } from '../types';

const OverviewTab: React.FC<OverviewTabProps> = ({ rounds, activeRound, userRole, onNewRound, onExportData }) => {
  const isAdmin = userRole === 'admin';
  const activeRoundsCount = rounds.filter(r => r.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Responsive: stat cards use 2 cols on tablet, 3 on desktop, 1 on mobile */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 sm:p-6 text-white hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100 font-medium">{isAdmin ? 'Total Rounds' : 'Assigned Rounds'}</p>
              <p className="text-3xl sm:text-4xl font-bold mt-2">{rounds.length}</p>
              <p className="text-xs text-blue-100 mt-1">{isAdmin ? 'Across all evaluators' : 'Assigned to you'}</p>
            </div>
            <ClipboardList className="w-10 h-10 sm:w-16 sm:h-16 text-blue-200 opacity-50 flex-shrink-0" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-4 sm:p-6 text-white hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-100 font-medium">Active Rounds</p>
              <p className="text-3xl sm:text-4xl font-bold mt-2">{activeRoundsCount}</p>
              <p className="text-xs text-green-100 mt-1">Currently running</p>
            </div>
            <PlayCircle className="w-10 h-10 sm:w-16 sm:h-16 text-green-200 opacity-50 flex-shrink-0" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-4 sm:p-6 text-white hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-100 font-medium">Total Comparisons</p>
              <p className="text-3xl sm:text-4xl font-bold mt-2">{activeRound?.totalComparisons || 0}</p>
              <p className="text-xs text-purple-100 mt-1">In active round</p>
            </div>
            <TrendingUp className="w-10 h-10 sm:w-16 sm:h-16 text-purple-200 opacity-50 flex-shrink-0" />
          </div>
        </div>

      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={onNewRound}
              className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium group cursor-pointer min-h-[44px]"
            >
              <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-150 flex-shrink-0" />
              <span className="text-base sm:text-lg">Start New Evaluation Round</span>
            </button>
            <button
              onClick={onExportData}
              className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium group cursor-pointer min-h-[44px]"
            >
              <Download className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-150 flex-shrink-0" />
              <span className="text-base sm:text-lg">Export Aggregate Rankings</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
